<script lang="ts" setup>
  import { computed, ref } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { BaseButton, BaseInput, formatDate } from '@qic/ui-components'
  // import { BaseButton, BaseInput, ToggleInput, formatDate } from '@qic/ui-components';
  import { useValidation, useValidators } from '@/shared/composables'
  import { useCommonStore, useFeaturesStore, useUserStore } from '@/shared/store'
  import FlowCard from '@/shared/components/FlowCard/FlowCard.vue'
  import PasportNumberInput from '@/entities/Flow/components/PasportNumberInput/PasportNumberInput.vue'
  import NationalityInput from '@/entities/Flow/components/NationalityInput/NationalityInput.vue'
  import BirthDateInput from '@/entities/Flow/components/BirthDateInput/BirthDateInput.vue'
  import type { TravelerInformation as ApiTravelerInformation } from '@/shared/types/api.types'

  import { getEmptyTravelerObject } from './FlowTravelerFormWidget.utils'
  // import BaseFoldingBlock from '@/shared/components/BaseFoldingBlock/BaseFoldingBlock.vue';
  import FlowDataProtectReminder from '@/entities/Flow/components/FlowDataProtectReminder'
  import { TravelDirection } from '@/shared/types/common.types'
  import { INBOUND_MAX_AGE, OUTBOUND_MAX_AGE } from './FlowTravelerForm.constants'
  import NewSelect from '@/widgets/Flow/NewBaseSelect/NewBaseSelect.vue'
  import { parseDateFromStr } from '@/shared/utils'

  interface Props {
    information?: ApiTravelerInformation | null
    withWinterSport?: boolean
    winterSportPrice?: number
    typeOfTravel?: TravelDirection
  }

  interface Emits {
    (e: 'save', information: ApiTravelerInformation): void
  }

  const props = withDefaults(defineProps<Props>(), {
    information: null,
    withWinterSport: false,
    winterSportPrice: 0,
    typeOfTravel: TravelDirection.OUTBOUND,
  })

  const emit = defineEmits<Emits>()

  const { t } = useI18n()

  const nationalities = computed(() => commonStore.nationalities)
  const relations = computed(() => commonStore.relations)

  const userStore = useUserStore()
  const featuresStore = useFeaturesStore()
  const isGeorgiaWinterCoverEnabled = computed(() => featuresStore.isGeorgiaWinterCoverEnabled)

  const commonStore = useCommonStore()
  const isEditMode = !!props.information

  const title = computed(() =>
    isEditMode ? t('travellers_update_info') : t('travellers_add_person')
  )

  // TODO: move to shared. May be create a singleton class.const getDateFromUnixStamp = (unix: string | number)  => new Date(Number(unix) * 1000);

  const travelerInformation = ref<ApiTravelerInformation>(getEmptyTravelerObject())
  const nationality = ref(props.information?.nationalityCode || '')
  const firstName = ref(props.information?.firstName || '')
  const lastName = ref(props.information?.lastName || '')
  const relation = ref(props.information?.relationCode || '')
  const dateOfBirth = ref(
    isEditMode && props.information?.dateOfBirth
      ? parseDateFromStr(props.information.dateOfBirth)
      : null
  )
  const passportNumber = ref(props.information?.passportNo || '')
  if (props.information) {
    travelerInformation.value = {
      ...props.information,
    }
  }
  const isOutbound = computed(() => props.typeOfTravel === TravelDirection.OUTBOUND)
  const maxAge = computed(() => (isOutbound.value ? OUTBOUND_MAX_AGE : INBOUND_MAX_AGE))

  const getWinSportsValue = (): number => {
    if (isGeorgiaWinterCoverEnabled.value && userStore.isGeorgiaDestination) {
      return props.information?.winSports !== undefined ? +props.information.winSports : 1
    }

    if (props.withWinterSport && props.information?.winSports !== undefined) {
      return +props.information.winSports
    }

    return 0
  }

  const onSave = () => {
    v$.value.$touch()
    if (v$.value.$error) return

    const oldInformation = props.information || getEmptyTravelerObject()
    const newInformation = {
      ...oldInformation,
      nationalityCode: nationality.value,
      nationalityName: String(
        nationalities.value.find(
          (nationalityValue) => nationalityValue.nationalityCode === nationality.value
        )?.nationalityName
      ),
      relationCode: relation.value,
      firstName: firstName.value.trim(),
      lastName: lastName.value.trim(),
      passportNo: passportNumber.value,
      relationName: String(
        relations.value.find((relationValue) => relationValue.code === relation.value)?.desc
      ),
      dateOfBirth: dateOfBirth.value ? formatDate(dateOfBirth.value, 'en', 'dd/MM/yyyy') : '',
      winSports: getWinSportsValue(),
    }

    emit('save', newInformation)
  }

  const { required } = useValidators()
  const validationRules = {
    relation: { required },
    firstName: { required },
    lastName: { required },
    passportNumber: { required },
    nationality: { required },
    dateOfBirth: { required },
  }

  const { v$, errors } = useValidation(validationRules, {
    relation,
    firstName,
    lastName,
    passportNumber,
    nationality,
    dateOfBirth,
  })
</script>

<template>
  <FlowCard :title="title" @button-click="onSave">
    <div class="traveler-form">
      <NewSelect
        v-model="relation"
        :options="relations"
        option-title="desc"
        option-value="code"
        :label="t('inbound_travelers_add_relationship')"
        filterable
        :disabled="!relations.length"
        :errors="errors.relation"
        class="flow-traveler-form-widget__relations"
      />
      <BaseInput
        v-model="firstName"
        mask="*"
        :mask-tokens="{ '*': { pattern: /[a-zA-Z\s'-]/, multiple: true } }"
        :label="t('inbound_travelers_add_first_name')"
        :errors="errors.firstName"
        :max-length="60"
      />
      <BaseInput
        v-model="lastName"
        mask="*"
        :mask-tokens="{ '*': { pattern: /[a-zA-Z\s'-]/, multiple: true } }"
        :label="t('inbound_travelers_add_last_name')"
        :errors="errors.lastName"
        :max-length="60"
      />
      <BirthDateInput
        v-model="dateOfBirth"
        :max-age="maxAge"
        variant="other"
        :label="t('inbound_travelers_add_dob')"
        :errors="errors.dateOfBirth"
      />
      <NationalityInput
        v-model="nationality"
        :options="nationalities"
        :label="t('inbound_travelers_add_nationality')"
        :errors="errors.nationality"
      />
      <PasportNumberInput
        v-model="passportNumber"
        :label="t('inbound_travelers_add_passport')"
        :errors="errors.passportNumber"
      />
      <!--
      	<BaseFoldingBlock v-if="props.withWinterSport" class="traveler-form__winter" is-open>
      	<template #title>
      	<div class="traveler-form__winter-title">
      	{{ $t('outbound_sport_cover_winter_name') }}
      	</div>
      	</template>

      	{{ $t('outbound_sport_cover_winter_description') }}

      	<template #after>
      	<div class="traveler-form__winter-toggle">
      	<div class="traveler-form__winter-price ltr">
      	+{{ $t('qar') }} {{ winterSportPrice }}
      	</div>
      	<ToggleInput @click.stop v-model="winterSports" />
      	</div>
      	</template>
      	</BaseFoldingBlock> 
      -->
    </div>
    <template #button>
      <BaseButton class="personal-information__button" icon="none" @click="onSave">
        {{ isEditMode ? $t('inbound_travelers_edit_save_button') : $t('travelers.add_button') }}
      </BaseButton>
    </template>
    <template #after>
      <div class="traveler-form__after-content">
        <FlowDataProtectReminder />
      </div>
    </template>
  </FlowCard>
</template>

<style lang="scss">
  @import './FlowTravelerFormWidget';
</style>
